import { FileText } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { CONTACT_ECOTIC } from '../../../constants/config';
import { hrefTelefon } from '../../../shared/utils/contact-ecotic';
import { formateazaMarime, mesajStareProtocol } from '../utils/protocol-descarcare';
import { useProtocolDeColaborare } from '../hooks/useProtocolDeColaborare';

/**
 * Protocolul de colaborare — cartonasul fisierului + actiunea (SP-03).
 *
 * Traieste in DOUA locuri, cu acelasi continut: pasul 4 din 4 al inscrierii, si
 * punctul de acces permanent din pagina de asteptare. Un document „care se
 * pastreaza" n-are voie sa existe doar intr-un pas prin care nu te mai poti
 * intoarce — cine a inchis fila ar ramane fara el pentru totdeauna.
 *
 * 🔴 O SINGURA componenta, nu doua copii. `variant` comuta DOAR ierarhia
 * butonului; textele sunt aceleasi. Doua copii ar diverge la primul text
 * schimbat, si atunci acelasi document ar spune doua lucruri in doua locuri.
 *
 * ⚠️ De ce `variant='permanent'` are butonul CONTURAT, nu plin: pe pagina de
 * asteptare actiunea principala e asteptarea, nu descarcarea. Verificat in cod
 * de S1: acolo legatura catre filmulet e deja conturata, iar butonul plin e cel
 * de la sfarsit. Un protocol plin ar face doua butoane pline pe un ecran al
 * carui subiect e „cererea ta e in verificare".
 *
 * 🔴 ZERO bifa „descarcat" — vezi comentariul din `useProtocolDeColaborare`.
 */

interface Props {
    /** `flux` = pasul 4 din 4 (buton plin) · `permanent` = pagina de asteptare (conturat). */
    variant: 'flux' | 'permanent';
}

/** `''` cand numarul configurat nu are nicio cifra — atunci ramane text, nu link mort. */
const TELEFON_HREF = hrefTelefon(CONTACT_ECOTIC.telefon);

const CLASE_CONTURAT =
    'min-h-11 w-full border border-input bg-white font-semibold text-dark hover:bg-light';

export default function CardProtocol({ variant }: Props) {
    const { stare, descarca, reincearca } = useProtocolDeColaborare();
    const permanent = variant === 'permanent';

    /* Cat timp nu stim, nu afirmam nimic: nici „gata", nici „nu e disponibil".
       Un buton activ desenat optimist ar fi o promisiune pe care n-am verificat-o. */
    if (stare === null) {
        return (
            <div className="rounded-lg border border-hairline bg-white p-3 text-small text-gray-600">
                Se verifică protocolul…
            </div>
        );
    }

    /* Ce s-a intamplat + ce se poate face, din modulul pur (testat acolo), in registrul cardului.
       Numarul de telefon se pune aici, ca link, nu in text. */
    const mesaj = mesajStareProtocol(stare, 'tu');
    const telefon = TELEFON_HREF ? (
        <a href={TELEFON_HREF} className="touch-target-link inline-flex items-center underline">
            {CONTACT_ECOTIC.telefon}
        </a>
    ) : (
        CONTACT_ECOTIC.telefon
    );
    const contact = mesaj?.indemnContact ? (
        <div className="mt-3 rounded-lg bg-light p-3 text-small text-dark">
            {mesaj.indemnContact} {telefon} (program: {CONTACT_ECOTIC.program}).
        </div>
    ) : null;

    /* Starea 2. 404 NU e o eroare rosie: o scoala fara protocol in ediția curentă
       e o stare normala, iar inscrierea e completa si fara el. */
    if (stare.stare === 'indisponibil') {
        return (
            /* 🔴 `data-protocol-motiv` — discriminantul pe ECRAN, nu doar in consola.
               Ecranul ramane identic in ambele cazuri (asta e cerinta, si S1 a masurat-o pe
               `7ae6be5e`: aceeasi stare, acelasi text, aceleasi butoane, la 360 si la 1280).
               Atributul nu se vede si nu se citeste de un cititor de ecran — e pentru sonda si
               pentru cine deschide inspectorul.

               De ce nu ajunge `console.warn`-ul pe care il face hook-ul: `vite.config.js` are
               `dropConsole = mode === "production" && VITE_DROP_CONSOLE !== "false"`, iar DEV
               se construieste cu ACEEASI comanda. Masurat de S1 pe acelasi commit — `npm run
               build`: **0** ocurente ale discriminantului in bundle; cu `VITE_DROP_CONSOLE=false`:
               **1**. Deci exact in mediul pentru care a fost scris, diagnosticul nu exista.

               ⚠️ Si nu e „inca un caz din familia «pare ca ruleaza si nu ruleaza»", desi seamana:
               acolo codul NU se executa (`@PreAuthorize`, transformerele, portile in paralel).
               Aici codul se executa corect, si **artefactul ii sterge efectul**. Nicio citire de
               sursa si niciun test pe modul pur nu putea sa-l prinda — doar o masuratoare pe
               bundle. De-aia reparatia sta in randare, nu in config: un `VITE_DROP_CONSOLE=false`
               pus pe un workflow repara mediul de azi si se pierde tacut la primul build facut
               altfel.

               🔴 Si o precizare fara de care istoricul se citeste gresit: atributul a fost
               VERIFICAT corect de S1 pe `025c2bb0` — dar valoarea pe care o purta era gresita,
               pentru ca `stareDinRaspuns` avea maparea inversata (vezi `protocol-descarcare.ts`).
               Un canal de diagnostic corect care transporta o valoare falsa e mai rau decat unul
               lipsa: „verificat" se refera la canal, nu la adevarul de la capatul lui. */
            /* 🔴 Textele s-au schimbat pe 2026-10-07, odata cu poarta din hook. Cat cererea pleca
               si inainte de aprobare, aici ajungea mai ales un cont in asteptare, iar ecranul ii
               spunea «nu e încă publicat» + «îți trimitem un email când apare» + un buton stins
               care «se activează când protocolul e publicat» — trei fraze despre o publicare care
               nu exista ca eveniment (PDF-ul se genereaza la cerere) si niciun lucru de facut.
               Contul neaprobat nu mai face cererea deloc (vede starea „neaprobat", mai jos). Aici
               ajunge doar o scoala APROBATA al carei protocol nu se poate genera acum: i se spune
               asta, are «Încearcă din nou», si un telefon daca tot nu merge. */
            <div data-protocol="indisponibil" data-protocol-motiv={stare.motiv}>
                <p className="text-fluid-base text-dark m-0">
                    {mesaj?.text} Înscrierea ta este completă și fără el.
                </p>
                {contact}
                <Button type="button" onClick={reincearca} className={`mt-3 ${CLASE_CONTURAT}`}>
                    Încearcă din nou
                </Button>
            </div>
        );
    }

    /* Starea „neaprobat" (409, P0-4): contul n-are inca scoala aprobata. Nu e o
       eroare tehnica — nimic nu se schimba din reincercare, se schimba doar cand
       un admin aproba cererea. Acelasi tratament vizual ca „indisponibil"
       (buton dezactivat + explicatie), text diferit: nu invita la reincercare.

       🔴 Fara randul „Butonul se activeaza dupa aprobarea scolii." (owner, 2026-09-19).
       Descria o tranzitie pe care ecranul asta n-o vede: la aprobare, profesorul pleaca de
       aici (resolverul il duce pe home), deci promisiunea nu se implinea sub ochii lui. Ce
       ramane adevarat si util e butonul stins si motivul din paragraf. */
    if (stare.stare === 'neaprobat') {
        return (
            <div data-protocol="neaprobat">
                {/* 🔴 UN SINGUR paragraf, nu paragraf + caseta gri (owner, 2026-09-19): cele
                    doua spun acelasi lucru — ca documentul nu vine acum — iar caseta il repeta
                    ca un al doilea subiect, 12 px mai jos. Textul de email se citeste SUS. */}
                <p className="text-fluid-base text-dark m-0">
                    Protocolul devine disponibil după ce școala ta este aprobată. Îți trimitem un
                    email când se întâmplă, și îl vei găsi oricând aici.
                </p>
                {/* `aria-disabled`, nu `disabled`: `disabled:opacity-50` din
                    `buttonVariants` sterge eticheta la 50 %, adica sub pragul de
                    contrast, exact cand omul are nevoie sa citeasca de ce nu merge. */}
                <Button
                    type="button"
                    aria-disabled
                    onClick={(e) => e.preventDefault()}
                    className="mt-3 min-h-11 w-full cursor-not-allowed bg-light text-dark hover:bg-light"
                >
                    Descarcă protocolul
                </Button>
            </div>
        );
    }

    const marime = stare.stare === 'gata' ? formateazaMarime(stare.marime) : null;
    const nume = stare.stare === 'gata' ? stare.nume : 'Protocol de colaborare';

    return (
        <div data-protocol={stare.stare}>
            <div className="flex items-start gap-3 rounded-lg border border-hairline bg-white p-3">
                <FileText className="size-9 shrink-0 text-magenta" aria-hidden />
                <div className="min-w-0">
                    <p className="m-0 break-words font-semibold text-dark">{nume}</p>
                    {/* 🔴 Marimea se citeste din raspuns. Cand nu se stie, randul
                        SE OMITE — un „180 KB" scris de noi ar fi o cifra pe care
                        n-a masurat-o nimeni, deghizata in grija pentru om. */}
                    <p className="m-0 mt-0.5 text-small text-gray-600">
                        PDF{marime ? ` · ${marime}` : ''}
                        {permanent ? ' · descarcă oricând' : ''}
                    </p>
                </div>
            </div>

            {stare.stare === 'esuat' ? (
                /* Doua cauze, doua texte (2026-10-07): „n-am ajuns la server" se repara de pe
                   telefon (conexiunea), „serverul n-a putut" nu — acolo ramane reincercarea si,
                   daca tine, telefonul ECOTIC. Inainte era o singura fraza pentru amandoua. */
                <div
                    data-eroare-protocol
                    role="alert"
                    className="mt-3 rounded-lg border border-error bg-error/10 p-3 text-small text-error"
                >
                    {mesaj?.text}
                    {mesaj?.indemnContact && (
                        <>
                            {' '}
                            {mesaj.indemnContact} {telefon}.
                        </>
                    )}
                </div>
            ) : (
                /* 🔴 Textul de aici s-a schimbat a doua oara pe 2026-09-17 (runda 2 ECOTIC,
                   decizie owner), si de doua ori din acelasi motiv: documentul are un circuit
                   real de semnatura si retur (SE-15/SE-16, 2026-09-16), iar varianta veche a
                   cardului (varianta `permanent`) ramasese sa spuna exact opusul — „nu trebuie
                   trimis inapoi". Fraza aia era corecta la b23 (protocol fara circuit) si a
                   devenit falsa fara sa fie atinsa cand procesul s-a schimbat.

                   `TextProtocol.blocuri(...)` (backend, runda 2) precompleteaza scoala, adresa,
                   profesorul coordonator si directorul din datele platformei — orice camp lipsa
                   ramane linie de completat de mana, niciodata scris ca fiind gol pe ecran. De
                   aceea textul de aici nu mai revendica „model orientativ" (sablon generic): ii
                   descrie identitatea (numele scolii, codul SE) fara sa presupuna ca fiecare
                   camp chiar a fost completat pentru scoala curenta — asta se vede abia in PDF,
                   dupa descarcare, acelasi motiv pentru care verbul ramane «Descarcă». */
                <div className="mt-3 rounded-lg bg-light p-3 text-small text-dark">
                    {permanent ? (
                        <>
                            Protocolul rămâne aici cât ține ediția.{' '}
                            <strong>Descarcă-l, dă-l la semnat</strong> reprezentantului legal al
                            școlii, apoi încarcă-l din Profil, la începutul proiectului.
                        </>
                    ) : (
                        <>
                            Protocolul poartă numele școlii tale și codul SE, cu datele completate
                            din platformă. Ce lipsește rămâne loc de completat de mână.
                        </>
                    )}
                </div>
            )}

            {stare.stare === 'esuat' ? (
                <>
                    <Button
                        type="button"
                        onClick={reincearca}
                        className={permanent ? `mt-3 ${CLASE_CONTURAT}` : 'mt-3 min-h-11 w-full'}
                    >
                        Încearcă din nou
                    </Button>
                    <p className="mt-2 text-center text-smaller text-gray-600 m-0">
                        Poți continua și fără el — înscrierea ta este completă.
                    </p>
                </>
            ) : (
                <>
                    <Button
                        type="button"
                        onClick={descarca}
                        className={permanent ? `mt-3 ${CLASE_CONTURAT}` : 'mt-3 min-h-11 w-full'}
                    >
                        Descarcă protocolul
                    </Button>
                    {/* Randul care tine locul bifei: e si adevarat, si util —
                        telefon acasa, calculator la scoala. */}
                    <p className="mt-2 text-center text-smaller text-gray-600 m-0">
                        Îl poți descărca de câte ori ai nevoie.
                    </p>
                </>
            )}
        </div>
    );
}
